// DESCRIPTION: Adds custom inspector controls (Event Type filter, Hide Past Events toggle)
// to the Nemtly Events query loop variation.

/**
 * WordPress dependencies
 */
import { addFilter } from '@wordpress/hooks';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, SelectControl, ToggleControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

/**
 * Check if a block is our Nemtly Events variation.
 *
 * @param {Object} props Block props.
 *
 * @return {boolean} True if this is the nemtly/events variation.
 */
function isNemtlyEventsVariation( props ) {
	return (
		props.name === 'core/query' &&
		props.attributes.namespace === 'nemtly/events'
	);
}

/**
 * Add custom controls to the Nemtly Events query variation.
 *
 * @param {Function} BlockEdit Original BlockEdit component.
 *
 * @return {Function} Wrapped BlockEdit component.
 */
function withNemtlyEventsControls( BlockEdit ) {
	return ( props ) => {
		if ( ! isNemtlyEventsVariation( props ) ) {
			return <BlockEdit { ...props } />;
		}

		const { attributes, setAttributes } = props;
		const { query } = attributes;

		const eventType = query.nemtlyEventType || '';
		const hidePastEvents = query.nemtlyHidePastEvents || false;

		const updateQuery = ( newQueryArgs ) => {
			setAttributes( {
				query: {
					...query,
					...newQueryArgs,
				},
			} );
		};

		return (
			<>
				<BlockEdit { ...props } />
				<InspectorControls>
					<PanelBody
						title={ __( 'Event Filters', 'nemtly-booking' ) }
						initialOpen={ true }
					>
						<SelectControl
							label={ __( 'Event Type', 'nemtly-booking' ) }
							value={ eventType }
							options={ [
								{
									label: __( 'All Types', 'nemtly-booking' ),
									value: '',
								},
								{
									label: __(
										'Events (date-based)',
										'nemtly-booking'
									),
									value: 'event',
								},
								{
									label: __(
										'One-on-One (slot-based)',
										'nemtly-booking'
									),
									value: 'one-on-one',
								},
								{
									label: __(
										'Group (slot-based)',
										'nemtly-booking'
									),
									value: 'group',
								},
							] }
							onChange={ ( value ) =>
								updateQuery( {
									nemtlyEventType: value,
								} )
							}
							help={ __(
								'Slot-based events (One-on-One, Group) do not have a fixed event date.',
								'nemtly-booking'
							) }
						/>
						<ToggleControl
							label={ __( 'Hide Past Events', 'nemtly-booking' ) }
							checked={ hidePastEvents }
							onChange={ ( value ) =>
								updateQuery( {
									nemtlyHidePastEvents: value,
								} )
							}
							help={
								hidePastEvents
									? __(
											'Only showing upcoming events.',
											'nemtly-booking'
									  )
									: __(
											'Showing all events including past.',
											'nemtly-booking'
									  )
							}
						/>
					</PanelBody>
				</InspectorControls>
			</>
		);
	};
}

addFilter(
	'editor.BlockEdit',
	'nemtly-booking/events-query-controls',
	withNemtlyEventsControls
);
